/**
 * Badge Component
 *
 * Status and label badges with semantic colors.
 * Uses design system getStatusClasses/getSeverityClasses for theme-aware colors.
 */

import React from 'react';
import { getStatusClasses, getSeverityClasses } from '../../lib/design';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
type BadgeSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  severity?: BadgeSeverity;
  size?: 'sm' | 'md';
  dot?: boolean;
  className?: string;
}

const neutralClasses = 'bg-black/[0.06] text-[var(--text-secondary)] border-[var(--border-default)]';

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

const dotColors: Record<string, string> = {
  success: 'bg-[#34C759]',
  warning: 'bg-[#FF9500]',
  error: 'bg-[#FF3B30]',
  info: 'bg-[#111111]',
  default: 'bg-[var(--text-tertiary)]',
  neutral: 'bg-[var(--text-tertiary)]',
};

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant,
  severity,
  size = 'sm',
  dot = false,
  className = '',
}) => {
  let colorClasses: string;

  if (severity) {
    colorClasses = getSeverityClasses(severity);
  } else if (variant && variant !== 'neutral' && variant !== 'default') {
    colorClasses = getStatusClasses(variant);
  } else {
    colorClasses = `inline-flex items-center rounded-full font-medium border ${neutralClasses}`;
  }

  const needsBaseStyles = variant === 'neutral' || variant === 'default' || (!severity && !variant);
  const baseClasses = needsBaseStyles
    ? `inline-flex items-center rounded-full font-medium border ${sizeClasses[size]}`
    : '';

  return (
    <span className={`whitespace-nowrap ${baseClasses} ${colorClasses} ${className}`.trim()}>
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotColors[variant || 'default']}`}
        />
      )}
      {children}
    </span>
  );
};
